import { supabase, supabaseConfigurado } from '../supabase/cliente'
import {
  db,
  limpiarBlobsSincronizadosAntiguos,
  renombrarProveedorLocal,
  type BorradoPendiente,
  type FotoProductoPendiente,
  type ProductoPendiente,
  type ProveedorPendiente,
} from './db'
import { esCodigoTemporal, extraerLetraDeCodigoTemporal } from '../../domain/codigo-proveedor'
import { demoUpsertProducto, demoListarProductos, demoUpsertProveedor } from '../demo/almacen-demo'
import type { Producto } from '../../domain/entidades'
import { hayConexionEstable } from '../util/conectividad'
import { sesionSupabaseReal } from '../auth/auth.service'
import { crearSubidorDeImagenes } from '../storage/subidor-de-imagenes'
import { SubidorSupabase } from '../storage/subidor-supabase'
import {
  rutaColorEstructuraProducto,
  rutaFotoGaleriaProducto,
  rutaTarjetaProveedor,
  rutaTelaFrente,
  rutaTelaReverso,
} from '../storage/rutas-storage'
import { environment } from '../../../environments/environment'

/** Sincronizamos la cola local con Supabase Storage/Postgres o con el modo demo. */

// Backend de fotos: Supabase por defecto, S3 automático en cuanto haya
// credenciales configuradas (ver crearSubidorDeImagenes). Todo lo demás en
// este archivo (cola offline, reintentos, límites por producto) no sabe ni
// le importa cuál de los dos es.
const subidorDeImagenes = crearSubidorDeImagenes(environment)

/**
 * `true` si el blob tiene contenido real. Los blobs se ponen en 0 bytes a
 * propósito 72h después de sincronizar (ver limpiarBlobsSincronizadosAntiguos
 * en db.ts, pensado para no acumular espacio local infinito) — si después de
 * eso se edita solo un campo de texto (ej. el nombre del proveedor) y se
 * resincroniza, NO hay que volver a subir esa foto: ya está bien en Storage,
 * y el blob local vacío no es un dato nuevo, es solo la limpieza haciendo su
 * trabajo. Sin este chequeo, `subirImagen` lanza "Foto corrupta o vacía" y
 * bloquea la sincronización entera de esa fila por una edición que ni
 * siquiera tocaba fotos (ver docs/hallazgo-fotos-duplicadas-al-editar-2026-09-05.md).
 */
function tieneContenido(buffer?: ArrayBuffer): buffer is ArrayBuffer {
  return Boolean(buffer && buffer.byteLength > 0)
}

/** Subimos una foto ya persistida (ArrayBuffer) al storage configurado. */
async function subirImagen(ruta: string, datos: ArrayBuffer): Promise<string> {
  // Defensa en profundidad: si llega vacio (blob corrupto en captura o al leer de
  // IndexedDB) cortamos aca con un mensaje util en vez del "No content provided"
  // crudo que devuelve Supabase Storage. Ver docs/hallazgo-no-content-provided-china.md.
  if (datos.byteLength === 0) {
    throw new Error('Foto corrupta o vacía, debes volver a tomarla')
  }
  return subidorDeImagenes.subir(ruta, datos, 'image/jpeg')
}

function blobAObjectUrl(buffer?: ArrayBuffer) {
  return buffer ? URL.createObjectURL(new Blob([buffer], { type: 'image/jpeg' })) : undefined
}

/** Normalizamos errores de Supabase y JavaScript a mensajes legibles. */
function mensajeDeError(err: unknown): string {
  if (err instanceof Error) return err.message
  if (err && typeof err === 'object') {
    const e = err as { message?: string; details?: string; hint?: string; code?: string }
    const partes = [e.message, e.details, e.hint].filter(Boolean)
    if (partes.length > 0) {
      const prefijo = e.code ? `[${e.code}] ` : ''
      return `${prefijo}${partes.join(' — ')}`
    }
  }
  if (typeof err === 'string') return err
  if (typeof err === 'number' || typeof err === 'boolean' || typeof err === 'bigint') return `${err}`
  return 'Error desconocido'
}

// ---------- Proveedores ----------

/**
 * Si el proveedor todavía tiene código TEMPORAL (ver domain/codigo-proveedor.ts),
 * reserva el código real y definitivo de forma atómica en Supabase (función
 * `reservar_codigo_proveedor`, ver docs/plan-fix-numeracion-proveedores-offline-online.md)
 * y renombra en cascada proveedor + productos + telas ANTES de que nada se
 * suba — así lo que se sincroniza ya lleva el código final, y el loop de
 * productos (que corre después de este, ver sincronizarPendientes) los sube
 * con la referencia correcta.
 *
 * Es naturalmente idempotente ante reintentos: si esta función ya corrió una
 * vez con éxito, `p.codigo` en Dexie ya no es temporal, así que una segunda
 * pasada no vuelve a pedir un código nuevo (no se "queman" números de más).
 */
async function resolverCodigoRealSiHaceFalta(p: ProveedorPendiente): Promise<string> {
  if (!esCodigoTemporal(p.codigo)) return p.codigo

  const letra = extraerLetraDeCodigoTemporal(p.codigo)
  const { data, error } = await supabase.rpc('reservar_codigo_proveedor', { p_letra: letra })
  if (error) throw error
  const codigoFinal = data as string

  await renombrarProveedorLocal(p.id!, p.codigo, codigoFinal)
  return codigoFinal
}

/**
 * @returns la URL/key nueva de la tarjeta si se subió una foto en esta
 * pasada, o `undefined` si no había nada nuevo que subir — el llamador la
 * persiste en Dexie (ver comentario de `tarjetaUrl` en db.ts: hace falta
 * saber en qué backend terminó cada foto para poder borrarla del lugar
 * correcto más adelante, no solo para el caso de blob vacío post-72h).
 */
async function sincronizarProveedorReal(p: ProveedorPendiente): Promise<{ tarjetaUrl?: string; codigoFinal: string }> {
  const codigoFinal = await resolverCodigoRealSiHaceFalta(p)

  // Antes esta URL se guardaba en la columna `notas`, pisando cualquier nota
  // real del proveedor — ahora va en su propia columna `foto_tarjeta_url`.
  //
  // Ruta ESTABLE (sin timestamp) a propósito — ver hallazgo-fotos-duplicadas-
  // al-editar-2026-09-05.md: si esto lleva Date.now(), cada vez que se reedita
  // y resincroniza el proveedor, sube un archivo NUEVO en vez de sobrescribir
  // el mismo, duplicando espacio en Storage para siempre. Con ruta fija,
  // resincronizar sobrescribe (SubidorSupabase ya sube con upsert:true; un
  // PUT a S3 sobrescribe por diseño) — nunca duplica.
  const tarjetaUrl = tieneContenido(p.tarjetaBlob)
    ? await subirImagen(rutaTarjetaProveedor(codigoFinal), p.tarjetaBlob)
    : undefined
  const { error } = await supabase.from('proveedores').upsert(
    {
      codigo: codigoFinal,
      nombre: p.nombre,
      ubicacion: p.ubicacion,
      contacto: p.contacto,
      telefono: p.telefono,
      ...(tarjetaUrl ? { foto_tarjeta_url: tarjetaUrl } : {}),
    },
    { onConflict: 'codigo' },
  )
  if (error) throw error
  return { tarjetaUrl, codigoFinal }
}

function sincronizarProveedorDemo(p: ProveedorPendiente): void {
  demoUpsertProveedor({
    codigo: p.codigo,
    nombre: p.nombre || p.codigo,
    ubicacion: p.ubicacion,
    contacto: p.contacto,
    telefono: p.telefono,
    fotoTarjetaUrl: blobAObjectUrl(p.tarjetaBlob),
  })
}

// ---------- Productos ----------

/**
 * Techo de fotos de producto que llegan a Supabase (Storage cuesta en el plan
 * free). En el celular se pueden capturar hasta 3 presets + 2 libres (5 en
 * total, ver galeria-fotos.ts), pero solo subimos estas 3 — nunca se pierde
 * ninguna localmente, solo no todas se sincronizan.
 */
const MAX_FOTOS_PRODUCTO_SYNC = 3
const ORDEN_PRESETS_PRODUCTO = ['General', 'Lado', 'Abajo']

/**
 * Priorizamos General → Lado → Abajo; si falta alguno de esos, completamos el
 * cupo de 3 con la siguiente foto disponible (tipicamente una libre). Si hay
 * menos de 3 fotos en total, subimos las que haya sin inventar ni bloquear.
 */
function seleccionarFotosParaSync(fotos: FotoProductoPendiente[]): FotoProductoPendiente[] {
  const restantes = [...fotos]
  const elegidas: FotoProductoPendiente[] = []

  for (const etiqueta of ORDEN_PRESETS_PRODUCTO) {
    const idx = restantes.findIndex((f) => f.etiqueta === etiqueta)
    if (idx === -1) continue
    elegidas.push(restantes[idx])
    restantes.splice(idx, 1)
  }

  while (elegidas.length < MAX_FOTOS_PRODUCTO_SYNC && restantes.length > 0) {
    elegidas.push(restantes.shift()!)
  }

  return elegidas
}

async function sincronizarProductoReal(p: ProductoPendiente): Promise<void> {
  // Subimos primero las fotos que deben quedar asociadas al producto.
  //
  // Rutas ESTABLES (sin timestamp) a propósito, acá y en el resto de esta
  // función — ver hallazgo-fotos-duplicadas-al-editar-2026-09-05.md. Editar
  // y resincronizar un producto ya guardado marca TODA la fila (fotos
  // incluidas, hayan cambiado o no) como pendiente de nuevo — con timestamp
  // en el nombre, cada resync subía un archivo nuevo y el viejo quedaba
  // huérfano para siempre. Con una ruta fija por foto/tela, resincronizar
  // sobrescribe el mismo archivo — nunca duplica.
  //
  // Además, tras 72h el blob local se vacía a propósito (ver
  // limpiarBlobsSincronizadosAntiguos) — tieneContenido() evita reintentar la
  // subida en ese caso (evita el error "Foto corrupta o vacía" al editar solo
  // un campo de texto). Para color/estructura y telas alcanza con omitir la
  // columna cuando no hay nada nuevo (Postgres no la toca, ver
  // docs/hallazgo-blob-vacio-post-72h-2026-09-06.md) — pero la galería
  // (`fotos`) es un arreglo que se reemplaza ENTERO en cada sync, así que ahí
  // hace falta reutilizar la URL real ya conocida (guardada localmente la
  // primera vez que se subió), no solo omitir.
  const colorEstructuraFotoUrl = tieneContenido(p.colorEstructuraFotoBlob)
    ? await subirImagen(rutaColorEstructuraProducto(p.productoId), p.colorEstructuraFotoBlob)
    : undefined

  // Convertimos la galería local en URLs públicas antes del upsert, priorizando
  // los angulos sugeridos y con un techo de 3 fotos por producto (ver
  // seleccionarFotosParaSync — el resto queda solo en el celular). Usamos el
  // id propio de cada foto (estable) para la ruta, no el índice del bucle —
  // el índice podría cambiar de una sincronización a otra si el orden de
  // `fotosASubir` varía.
  const fotosASubir = seleccionarFotosParaSync(p.fotosBlobs)
  const fotos: { url: string; etiqueta: string }[] = []
  // Acumulamos qué foto quedó con qué URL nueva, para guardarlo localmente
  // después — sin esto, la próxima vez que el blob esté vacío no habría
  // forma de saber la URL real sin adivinar (¿S3 o el respaldo de Supabase?).
  const urlsNuevasPorFotoId = new Map<string, string>()
  for (const foto of fotosASubir) {
    if (tieneContenido(foto.blob)) {
      const url = await subirImagen(rutaFotoGaleriaProducto(p.productoId, foto.id), foto.blob)
      urlsNuevasPorFotoId.set(foto.id, url)
      fotos.push({ url, etiqueta: foto.etiqueta })
    } else if (foto.url) {
      // Blob vacío (ya sincronizado hace +72h) pero ya sabemos su URL real.
      fotos.push({ url: foto.url, etiqueta: foto.etiqueta })
    }
    // Si no hay blob ni URL conocida, no hay nada que mostrar — se omite.
  }

  const { error: errorProducto } = await supabase.from('productos').upsert(
    {
      id: p.productoId,
      proveedor_codigo: p.proveedorCodigo,
      mueble: p.mueble,
      categoria: p.categoria,
      fob: p.fob,
      moq: p.moq ? Number(p.moq) || null : null,
      unidades_contenedor: p.unidadesPorContenedor ? Number(p.unidadesPorContenedor) || null : null,
      notas: p.notas,
      atributos_especiales: p.atributosEspeciales,
      fotos,
      color_estructura: p.colorEstructura,
      ...(colorEstructuraFotoUrl ? { color_estructura_foto_url: colorEstructuraFotoUrl } : {}),
    },
    { onConflict: 'id' },
  )
  // Cortamos aquí para no insertar telas si el producto no quedó persistido.
  if (errorProducto) throw errorProducto

  // Acumulamos también las URLs nuevas de color/estructura y de cada tela —
  // mismo motivo que la galería (saber en qué backend terminó cada foto, ver
  // BackendStorage en db.ts), pero antes no se guardaban en ningún lado: solo
  // se usaban para el upsert de Postgres y se perdían. Necesarias para poder
  // borrar del storage correcto en el borrado en cascada (casos-de-uso.ts).
  const urlsNuevasFrentePorTelaId = new Map<string, string>()
  const urlsNuevasReversoPorTelaId = new Map<string, string>()

  for (const tela of p.telas) {
    const fotoFrenteUrl = tieneContenido(tela.fotoFrenteBlob)
      ? await subirImagen(rutaTelaFrente(p.productoId, tela.id), tela.fotoFrenteBlob)
      : undefined
    const fotoReversoUrl = tieneContenido(tela.fotoReversoBlob)
      ? await subirImagen(rutaTelaReverso(p.productoId, tela.id), tela.fotoReversoBlob)
      : undefined
    if (fotoFrenteUrl) urlsNuevasFrentePorTelaId.set(tela.id, fotoFrenteUrl)
    if (fotoReversoUrl) urlsNuevasReversoPorTelaId.set(tela.id, fotoReversoUrl)

    const { error } = await supabase.from('telas').upsert(
      {
        id: tela.id,
        producto_id: p.productoId,
        muestrario: tela.muestrario,
        colores: tela.colores.join(', '),
        tipo: tela.tipo,
        ...(fotoFrenteUrl ? { foto_frente_url: fotoFrenteUrl } : {}),
        ...(fotoReversoUrl ? { foto_reverso_url: fotoReversoUrl } : {}),
      },
      { onConflict: 'producto_id,id' },
    )
    if (error) throw error
  }

  // Un solo update a Dexie con todo lo que haya cambiado esta pasada (galería,
  // color/estructura, telas) — pase lo que pase con cada uno individualmente,
  // lo que sí se subió de verdad conviene recordarlo igual.
  const huboAlgoNuevo =
    urlsNuevasPorFotoId.size > 0 || urlsNuevasFrentePorTelaId.size > 0 || urlsNuevasReversoPorTelaId.size > 0 || colorEstructuraFotoUrl

  if (huboAlgoNuevo && p.id !== undefined) {
    const fotosBlobsActualizadas = p.fotosBlobs.map((foto) => {
      const urlNueva = urlsNuevasPorFotoId.get(foto.id)
      return urlNueva ? { ...foto, url: urlNueva } : foto
    })
    const telasActualizadas = p.telas.map((tela) => ({
      ...tela,
      ...(urlsNuevasFrentePorTelaId.has(tela.id) ? { fotoFrenteUrl: urlsNuevasFrentePorTelaId.get(tela.id) } : {}),
      ...(urlsNuevasReversoPorTelaId.has(tela.id) ? { fotoReversoUrl: urlsNuevasReversoPorTelaId.get(tela.id) } : {}),
    }))
    await db.productos.update(p.id, {
      fotosBlobs: fotosBlobsActualizadas,
      telas: telasActualizadas,
      ...(colorEstructuraFotoUrl ? { colorEstructuraFotoUrl } : {}),
    })
  }
}

function sincronizarProductoDemo(p: ProductoPendiente): void {
  const previo = demoListarProductos().find((x) => x.id === p.productoId)
  const producto: Producto = {
    id: p.productoId,
    proveedorCodigo: p.proveedorCodigo,
    mueble: p.mueble,
    categoria: p.categoria,
    fob: p.fob,
    nombreComercial: previo?.nombreComercial ?? '',
    notas: p.notas,
    atributosEspeciales: p.atributosEspeciales,
    rango: previo?.rango,
    precioVentaCol: previo?.precioVentaCol,
    precioVentaPan: previo?.precioVentaPan,
    fotos: p.fotosBlobs.map((f) => ({ url: blobAObjectUrl(f.blob) ?? '', etiqueta: f.etiqueta })),
    colorEstructura: p.colorEstructura,
    colorEstructuraFotoUrl: blobAObjectUrl(p.colorEstructuraFotoBlob),
    telas: p.telas.map((t) => ({
      id: t.id,
      productoId: p.productoId,
      muestrario: t.muestrario,
      colores: t.colores.join(', '),
      tipo: (t.tipo ?? 'Otro') as Producto['telas'][number]['tipo'],
      fotoFrenteUrl: blobAObjectUrl(t.fotoFrenteBlob),
      fotoReversoUrl: blobAObjectUrl(t.fotoReversoBlob),
    })),
  }
  demoUpsertProducto(producto)
}

// ---------- Borrados ----------

// Instancia "cruda" (no la que decide S3-con-fallback-a-Supabase) para el
// borrado — 2026-09-07: a diferencia de subir (donde no se sabe de antemano
// cuál va a responder), al borrar SÍ sabemos exactamente en qué backend vive
// cada foto (ver BackendStorage/backendDeUrl) — no tiene sentido intentar en
// el que no es.
const _supabaseParaBorrar = new SubidorSupabase()

/**
 * DECISIÓN (2026-09-07, validada con el usuario y su DevOps): las fotos de
 * S3 NO se borran físicamente a propósito — ni con `s3:DeleteObject` (el rol
 * de la Lambda no lo tiene y DevOps prefiere no dártelo) ni sería posible
 * igual desde el navegador (el CORS del bucket solo permite
 * `PUT, GET, HEAD, POST`, no `DELETE` — confirmado en pruebas reales, dio
 * "Failed to fetch").
 *
 * La sugerencia de DevOps fue "manejarlo con un estado" — y eso es
 * exactamente lo que ya hace este sistema del lado de Postgres: en cuanto se
 * borra la fila (`productos`/`proveedores`/`telas`), ninguna parte de la app
 * vuelve a pedir esa foto nunca más — la ausencia de la fila ES el estado.
 * El archivo queda huérfano en S3 (ocupa espacio, invisible para todo el
 * mundo) — costo aceptado, no un bug. Si algún día importa limpiarlo, es
 * trabajo de DevOps del lado de AWS (ej. una regla de expiración por
 * antigüedad), no algo que la app deba resolver.
 *
 * Supabase Storage sí sigue borrándose de verdad (permiso confirmado, sin
 * problema de CORS) — solo S3 queda como huérfano intencional.
 */
async function borrarPorBackendReal(rutas: BorradoPendiente['rutasStorage']): Promise<void> {
  const rutasSupabase = rutas.filter((r) => r.backend === 'supabase').map((r) => r.ruta)
  if (rutasSupabase.length > 0) await _supabaseParaBorrar.borrar(rutasSupabase)
}

/**
 * Aplica un borrado pendiente contra Supabase (Storage + Postgres). Se llama
 * solo para filas con `creadoRemoto: true` en su momento (ver casos-de-uso.ts)
 * — si nunca se sincronizó, ni siquiera se encola acá, se borra directo de
 * Dexie sin pasar por Supabase.
 *
 * Borramos Storage antes que Postgres a propósito: si Storage falla y
 * cortamos acá, la fila de Postgres sigue existiendo y el borrado se
 * reintenta completo la próxima vez (rutasStorage es la misma, no hay riesgo
 * de "borrar Storage dos veces" — es idempotente). Al revés (Postgres primero)
 * un fallo de Storage dejaría archivos huérfanos sin ninguna fila que los
 * referencie y sin more forma de reintentarlo.
 */
async function aplicarBorradoReal(b: BorradoPendiente): Promise<void> {
  if (b.rutasStorage.length > 0) await borrarPorBackendReal(b.rutasStorage)

  if (b.tipo === 'tela') {
    const { error } = await supabase.from('telas').delete().eq('producto_id', b.productoId!).eq('id', b.telaId!)
    if (error) throw error
  } else if (b.tipo === 'producto') {
    // Borramos telas explícitamente en vez de confiar en un ON DELETE CASCADE
    // de la FK (no verificado en el esquema) — a la defensiva: si la cascada
    // sí existe, este delete simplemente no encuentra filas y no hace nada.
    const { error: errorTelas } = await supabase.from('telas').delete().eq('producto_id', b.productoId!)
    if (errorTelas) throw errorTelas
    const { error: errorProducto } = await supabase.from('productos').delete().eq('id', b.productoId!)
    if (errorProducto) throw errorProducto
  } else {
    const { error } = await supabase.from('proveedores').delete().eq('codigo', b.codigoProveedor!)
    if (error) throw error
  }
}

/**
 * Procesamos en 3 pasadas (tela → producto → proveedor) para nunca intentar
 * borrar un padre en Postgres antes que sus hijos — aunque las filas se
 * hayan encolado en cualquier orden (la cascada de eliminarProveedor() encola
 * primero las telas y productos, pero no vale la pena depender de eso).
 */
async function procesarBorrados(): Promise<ResultadoSincronizacion> {
  let exitosas = 0
  let fallidas = 0
  for (const tipo of ['tela', 'producto', 'proveedor'] as const) {
    const pendientes = await db.borradosPendientes.where('tipo').equals(tipo).toArray()
    for (const b of pendientes) {
      try {
        if (supabaseConfigurado) await aplicarBorradoReal(b)
        await db.borradosPendientes.delete(b.id!)
        exitosas += 1
      } catch (err) {
        await db.borradosPendientes.update(b.id!, { intentos: b.intentos + 1, ultimoError: mensajeDeError(err) })
        fallidas += 1
      }
    }
  }
  return { exitosas, fallidas }
}

export interface ResultadoSincronizacion {
  exitosas: number
  fallidas: number
}

export async function sincronizarPendientes(): Promise<ResultadoSincronizacion> {
  if (supabaseConfigurado && !sesionSupabaseReal()) {
    return { exitosas: 0, fallidas: 0 }
  }

  let exitosas = 0
  let fallidas = 0

  // sesionSupabaseReal no se apaga al perder wifi, asi que confirmamos conectividad real antes de subir.
  if (supabaseConfigurado && !(await hayConexionEstable())) {
    const sinConexion = new Error('Failed to fetch: sin conexión a internet')
    // .each() en vez de .toArray(): recorre la coleccion sin cargar todos los
    // registros (con sus fotos en ArrayBuffer) a memoria de una sola vez. Con
    // varios cientos de fotos pendientes (varios dias sin sincronizar), un
    // .toArray() podia significar cientos de MB en RAM simultaneos — riesgo
    // real de lentitud/crash en celulares de gama media/baja.
    await db.proveedores
      .where('sincronizado')
      .equals(0)
      .each(async (p) => {
        await db.proveedores.update(p.id!, { intentos: p.intentos + 1, ultimoError: mensajeDeError(sinConexion) })
        fallidas += 1
      })
    await db.productos
      .where('sincronizado')
      .equals(0)
      .each(async (p) => {
        await db.productos.update(p.id!, { intentos: p.intentos + 1, ultimoError: mensajeDeError(sinConexion) })
        fallidas += 1
      })
    await db.borradosPendientes.toCollection().each(async (b) => {
      await db.borradosPendientes.update(b.id!, { intentos: b.intentos + 1, ultimoError: mensajeDeError(sinConexion) })
      fallidas += 1
    })
    return { exitosas, fallidas }
  }

  await db.proveedores
    .where('sincronizado')
    .equals(0)
    .each(async (p) => {
      try {
        let tarjetaUrlNueva: string | undefined
        if (supabaseConfigurado) {
          ;({ tarjetaUrl: tarjetaUrlNueva } = await sincronizarProveedorReal(p))
        } else {
          sincronizarProveedorDemo(p)
        }
        // creadoRemoto queda en true para siempre desde el primer éxito — ver
        // comentario del campo en db.ts (lo necesita eliminarProveedor()).
        // El `codigo` final (si era temporal) ya quedó persistido en Dexie
        // por `renombrarProveedorLocal` — no hace falta repetirlo acá.
        await db.proveedores.update(p.id!, {
          sincronizado: 1,
          creadoRemoto: true,
          ...(tarjetaUrlNueva ? { tarjetaUrl: tarjetaUrlNueva } : {}),
        })
        exitosas += 1
      } catch (err) {
        await db.proveedores.update(p.id!, { intentos: p.intentos + 1, ultimoError: mensajeDeError(err) })
        fallidas += 1
      }
    })

  await db.productos
    .where('sincronizado')
    .equals(0)
    .each(async (p) => {
      try {
        if (supabaseConfigurado) await sincronizarProductoReal(p)
        else sincronizarProductoDemo(p)
        // Ver comentario equivalente en el bloque de proveedores de arriba.
        await db.productos.update(p.id!, { sincronizado: 1, creadoRemoto: true })
        exitosas += 1
      } catch (err) {
        await db.productos.update(p.id!, { intentos: p.intentos + 1, ultimoError: mensajeDeError(err) })
        fallidas += 1
      }
    })

  const resultadoBorrados = await procesarBorrados()
  exitosas += resultadoBorrados.exitosas
  fallidas += resultadoBorrados.fallidas

  // Oportunista, no bloqueante: aprovechamos que ya hay red para barrer fotos
  // de registros sincronizados hace mas de 72h (ver limpiarBlobsSincronizadosAntiguos
  // en db.ts). No afecta el resultado de esta sincronizacion si falla.
  void limpiarBlobsSincronizadosAntiguos().catch((err) =>
    console.error('[offline] no se pudo limpiar blobs sincronizados antiguos:', err),
  )

  return { exitosas, fallidas }
}

/** Avisamos a la UI cuando termina una sincronización automática. */
export const EVENTO_SINCRONIZADO = 'feria-china-2026:sincronizado'

// Reintentamos con backoff porque `navigator.onLine` no garantiza que Supabase responda.
const RETRASOS_REINTENTO_MS = [5_000, 15_000, 30_000]

async function sincronizarConReintentos(): Promise<void> {
  let intento = 0
  while (true) {
    if (supabaseConfigurado && !sesionSupabaseReal()) return
    // Confirmamos conectividad real antes de recorrer la cola y subir fotos.
    if (await hayConexionEstable()) {
      const resultado = await sincronizarPendientes()
      window.dispatchEvent(new CustomEvent(EVENTO_SINCRONIZADO, { detail: resultado }))
      if (resultado.fallidas === 0) return
    }
    if (intento >= RETRASOS_REINTENTO_MS.length) return
    await new Promise((r) => setTimeout(r, RETRASOS_REINTENTO_MS[intento]))
    intento += 1
  }
}

/** Registramos la sincronización automática al recuperar conexión. */
export function activarSincronizacionAutomatica() {
  window.addEventListener('online', () => {
    void sincronizarConReintentos()
  })
}
